import { IRocketTokens, spErrorTags } from "@eventr/common";
import { create } from "zustand";
import { devtools, subscribeWithSelector } from "zustand/middleware";
import { DEVTOOLS_ENABLED } from "../../consts";
import type EmbeddedChatApi from "../../feat/rocketchat/chatBody/EmbeddedChatApi";
import shareExtensionListener from "../../feat/ios-share-extension/shareExtensionListener";

interface RocketchatUiState {
  /** The ONE rocketchat token matching the selected org. */
  liveToken: IRocketTokens | undefined;
  /** How chat-room should reposition the scroll container after the next message batch. */
  scrollStyle: "auto" | "stationary" | "none";
  /**
   * The group-name field in NavigationView's search bar. `undefined` is reachable — the
   * field's own `useState` starts undefined and NavigationView mirrors it verbatim — and
   * chat-new distinguishes it from `""` when deciding whether there are unsaved changes,
   * so the type is deliberately wider than the old `signal<string>("")` claimed.
   */
  groupChatName: string | undefined;
  /** Chat name from a push notification payload, shown until the room's own name loads. */
  chatFallBackName: string;
  /** Set by chat-footer on submit; consumed once by the websocket message listener. */
  sentMessage: boolean;
  /** The shared EmbeddedChatApi instance, owned by the websocket lifecycle. */
  rocketchatBaseApi: EmbeddedChatApi | null;

  setLiveToken: (liveToken: IRocketTokens | undefined) => void;
  setScrollStyle: (scrollStyle: "auto" | "stationary" | "none") => void;
  setGroupChatName: (groupChatName: string | undefined) => void;
  setChatFallBackName: (chatFallBackName: string) => void;
  setSentMessage: (sentMessage: boolean) => void;
  setRocketchatBaseApi: (rocketchatBaseApi: EmbeddedChatApi | null) => void;
}

// Replaces the five preact signals in rocketchatSignals.tsx plus `rocketchatBaseApi`,
// which used to live in rocketChatProvider.tsx. Moving it here also breaks the
// rocketchat.slice -> rocketChatProvider -> bound.store -> rocketchat.slice import cycle.
//
// `create` comes from "zustand" directly, NOT feat/appState/resetState: that wrapper
// registers stores for `resetAllStores()` on logout, and the signals were never reset.
// `liveToken` in particular is cleared explicitly by the websocket teardown, not by logout.
export const useRocketchatUiStore = create<RocketchatUiState>()(
  subscribeWithSelector(
    devtools(
      (set, get) => ({
        liveToken: undefined,
        scrollStyle: "auto",
        groupChatName: "",
        chatFallBackName: "",
        sentMessage: false,
        rocketchatBaseApi: null,

        setLiveToken: (liveToken) => set({ liveToken }, false, "setLiveToken"),
        setScrollStyle: (scrollStyle) => set({ scrollStyle }, false, "setScrollStyle"),
        setGroupChatName: (groupChatName) => set({ groupChatName }, false, "setGroupChatName"),
        setChatFallBackName: (chatFallBackName) => set({ chatFallBackName }, false, "setChatFallBackName"),
        setSentMessage: (sentMessage) => set({ sentMessage }, false, "setSentMessage"),
        // Every reconnect (and the token swap at start-up) builds a new API with its own SDK
        // socket; the replaced one was never closed, so each kept a live server connection
        // and delivered every room event again (an edit arrived three times).
        setRocketchatBaseApi: (rocketchatBaseApi) => {
          const previous = get().rocketchatBaseApi;
          set({ rocketchatBaseApi }, false, "setRocketchatBaseApi");
          if (previous && previous !== rocketchatBaseApi) {
            previous.close().catch((error) => spErrorTags("Error closing the previous chat API", ["rocketchat"], error));
          }
        },
      }),
      { enabled: DEVTOOLS_ENABLED, name: "rocketchatUi" },
    ),
  ),
);

/**
 * Non-reactive read of the live token.
 *
 * Every `liveToken.value` read outside a render — websocket callbacks, effects,
 * `markAsRead`, `canOpenWebsocket` — was an *untracked* read under signals, because
 * signals only auto-track during render. `getLiveToken()` is the exact equivalent.
 * Use `useLiveToken()` instead for anything read during render.
 */
export const getLiveToken = () => useRocketchatUiStore.getState().liveToken;

export const useLiveToken = () => useRocketchatUiStore((s) => s.liveToken);
export const useGroupChatName = () => useRocketchatUiStore((s) => s.groupChatName);
export const useChatFallBackName = () => useRocketchatUiStore((s) => s.chatFallBackName);
export const useRocketchatBaseApi = () => useRocketchatUiStore((s) => s.rocketchatBaseApi);

// Was `effect(shareExtensionListener)`, kept at the same module position so import-graph
// timing is unchanged. The listener bails out on non-iOS before reading anything, so
// `liveToken` was its only tracked dependency; `fireImmediately` reproduces effect()'s
// immediate first run, and subscribeWithSelector runs subscribers synchronously inside
// set(), exactly as a signal write did.
useRocketchatUiStore.subscribe((s) => s.liveToken, () => shareExtensionListener(), { fireImmediately: true });
